// src/pages/CompetitionDetail.js - one competition: categories and the registration wizard.
//
// Audit fixes: FL-01 (wizard in the shared Modal: scrolls, footer always visible, sheet on
// phones), FL-06 (a submit error stays inside the wizard, the entered data is kept and
// also saved as a draft in sessionStorage), FL-08 (DancerPicker with real checkboxes,
// invalid dancers not selectable), FL-02 (backend reason shown), FL-07 (retry),
// FL-19 (labels), FL-21 (registration state, no "Přihlásit" on closed competitions,
// opening date and deadline), FL-46 (router Link, max 50 enforced, filters, own entries).
// After the registration deadline the round is "closed" (no registration buttons) even while
// its status is still "active". The wizard asks the server whether the choreography name is
// locked to a higher league (moved there by the jury) and blocks such an entry early.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CalendarDays, Check, ChevronRight, MapPin, Trophy } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import useFetch from '../hooks/useFetch';
import api, { choreographiesApi, competitionsApi, dancersApi, getErrorMessage } from '../services/api';
import {
  AGE_CATEGORIES,
  LEAGUES,
  ageCategoryLabel,
  categoryLabel,
  disciplineLabel,
  leagueLabel,
} from '../constants/categories';
import { formatDate, formatDateLong, countLabel, isPastCompetition, isRegistrationClosed } from '../utils/format';
import { readJSON, removeKey, writeJSON } from '../utils/storage';
import { Alert, Button, Modal, PageHeader, Spinner, StatusBadge, useToast } from '../components/ui';
import BackButton from '../components/layout/BackButton';
import DancerPicker, { evaluateSelection } from '../components/leader/DancerPicker';
import { useMeta } from '../contexts/MetaContext';
import TextField from '../components/leader/TextField';
import LoadError from '../components/leader/LoadError';
import {
  CapacityBadge,
  DeadlineText,
  RegistrationBadge,
  CompetitionTypeBadge,
  isFull,
} from '../components/leader/CompetitionBadges';

const WIZARD_STEPS = ['Údaje', 'Tanečníci', 'Kontrola'];
const EMPTY_WIZARD = { open: false, category: null, step: 1, name: '', choreographer: '', dancers: [] };
const LEGACY_LEAGUES = { 'ZÁKLADNÍ': 'BASIC', PROFI: 'PROFESSIONAL' };
const normLeague = (league) => LEGACY_LEAGUES[league] || league;
const sameCategory = (a, b) =>
  a && b && a.discipline === b.discipline && a.ageCategory === b.ageCategory && normLeague(a.league) === normLeague(b.league);
// The league check runs this long after the leader stops typing the name.
const LEAGUE_CHECK_DELAY = 400;
const leagueCheckKey = (name, category) =>
  category ? [category.discipline, category.ageCategory, normLeague(category.league), name.trim()].join('|') : null;

/** open | pre | full | closed | finished */
function registrationMode(competition) {
  if (!competition) return 'closed';
  if (isPastCompetition(competition)) return 'finished'; // took place, whatever its status says
  if (['finished', 'archived'].includes(competition.status)) return 'finished';
  // Deadline passed (or closed by the organiser): no new entries even while "active".
  if (isRegistrationClosed(competition)) return 'closed';
  if (competition.status === 'active') return isFull(competition) ? 'full' : 'open';
  if (competition.status === 'upcoming') return 'pre';
  return 'finished';
}

function Stepper({ step }) {
  return (
    <ol className="mb-5 flex items-center gap-2 text-sm" aria-label="Kroky registrace">
      {WIZARD_STEPS.map((label, index) => {
        const number = index + 1;
        const done = number < step;
        const current = number === step;
        return (
          <li key={label} className="flex items-center gap-2" aria-current={current ? 'step' : undefined}>
            <span
              className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                current ? 'bg-indigo-600 text-white' : done ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-200 text-gray-700'
              }`}
            >
              {done ? <Check className="h-4 w-4" aria-hidden="true" /> : number}
            </span>
            <span className={`${current ? 'font-medium text-gray-900' : 'text-gray-600'} ${current ? '' : 'hidden sm:inline'}`}>
              {label}
              {done && <span className="sr-only"> (hotovo)</span>}
            </span>
            {number < WIZARD_STEPS.length && <span className="h-px w-6 bg-gray-300 sm:w-10" aria-hidden="true" />}
          </li>
        );
      })}
    </ol>
  );
}

const CompetitionDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const meta = useMeta();
  const draftKey = `bob:registration-draft:${user?.id || 'anon'}:${id}`;

  const { data, error, isLoading, reload } = useFetch(async () => {
    const minePromise = choreographiesApi
      .getAll()
      .then((res) => res.data || [])
      .catch(() => []);
    const [competitionRes, dancersRes, mine] = await Promise.all([
      competitionsApi.getById(id),
      dancersApi.getAll(),
      minePromise,
    ]);
    return { competition: competitionRes.data, dancers: dancersRes.data || [], mine };
  }, id);

  const competition = data?.competition;
  const dancers = useMemo(() => data?.dancers || [], [data]);
  const myEntries = useMemo(
    () => (data?.mine || []).filter((c) => c.competition && String(c.competition.id) === String(id)),
    [data, id]
  );

  const [wizard, setWizard] = useState(EMPTY_WIZARD);
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const nameRef = useRef(null);
  const [draft, setDraft] = useState(() => readJSON(draftKey));
  const [ageFilter, setAgeFilter] = useState('');
  const [leagueFilter, setLeagueFilter] = useState('');
  // League lock (a choreography moved up by the jury may not enter a lower league): the last
  // answer of GET /choreographies/league-check, answers cached per name + category.
  const [leagueCheck, setLeagueCheck] = useState({ key: null, problem: null });
  const [checkingLeague, setCheckingLeague] = useState(false);
  const leagueAnswers = useRef(new Map());

  /** Problem text when the name is locked to another league, else null. Network errors are
   *  ignored - the server refuses such an entry on submit anyway. */
  const checkLeague = useCallback(async (name, category) => {
    const key = leagueCheckKey(name, category);
    if (!key || !name.trim()) return null;
    if (leagueAnswers.current.has(key)) {
      const known = leagueAnswers.current.get(key);
      setLeagueCheck({ key, problem: known });
      return known;
    }
    try {
      const res = await api.get('/choreographies/league-check', {
        params: {
          name: name.trim(),
          discipline: category.discipline,
          ageCategory: category.ageCategory,
          league: normLeague(category.league),
        },
      });
      const problem = (res.data && typeof res.data.problem === 'string' && res.data.problem) || null;
      leagueAnswers.current.set(key, problem);
      setLeagueCheck({ key, problem });
      return problem;
    } catch (err) {
      return null;
    }
  }, []);

  // Check the name shortly after the leader stops typing it.
  useEffect(() => {
    if (!wizard.open || wizard.step !== 1 || !wizard.category || !wizard.name.trim()) return undefined;
    const timer = setTimeout(() => checkLeague(wizard.name, wizard.category), LEAGUE_CHECK_DELAY);
    return () => clearTimeout(timer);
  }, [wizard.open, wizard.step, wizard.name, wizard.category, checkLeague]);

  // Keep the wizard as a draft, so a reload or an expired login does not lose it (FL-06).
  useEffect(() => {
    if (!wizard.open || !wizard.category) return;
    const snapshot = {
      category: wizard.category,
      step: wizard.step,
      name: wizard.name,
      choreographer: wizard.choreographer,
      dancers: wizard.dancers,
      savedAt: new Date().toISOString(),
    };
    writeJSON(draftKey, snapshot);
    setDraft(snapshot);
  }, [wizard, draftKey]);

  const closeWizard = useCallback(() => {
    if (submittingRef.current) return;
    setWizard((w) => ({ ...w, open: false }));
    setSubmitError(null);
  }, []);

  const discardDraft = () => {
    removeKey(draftKey);
    setDraft(null);
  };

  if (isLoading && !competition) return <Spinner label="Načítám soutěž…" />;
  if (error && !competition) {
    return <LoadError title="Soutěž se nepodařilo načíst" error={error} onRetry={reload} />;
  }
  if (!competition) return null;

  const mode = registrationMode(competition);
  const canCreate = mode === 'open' || mode === 'pre';
  const isGrandFinale = competition.isGrandFinale || competition.type === 'grand_finale';
  const categories = competition.availableCategories || [];
  const filtered = categories.filter(
    (c) => (!ageFilter || c.ageCategory === ageFilter) && (!leagueFilter || normLeague(c.league) === leagueFilter)
  );
  const ageGroups = AGE_CATEGORIES.map((age) => ({ age, items: filtered.filter((c) => c.ageCategory === age) })).filter(
    (g) => g.items.length > 0
  );
  const otherItems = filtered.filter((c) => !AGE_CATEGORIES.includes(c.ageCategory));
  if (otherItems.length) ageGroups.push({ age: 'OTHER', items: otherItems });
  const entriesIn = (category) => myEntries.filter((c) => sameCategory(c, category)).length;
  const draftUsable =
    canCreate && !isGrandFinale && draft && draft.category && categories.some((c) => sameCategory(c, draft.category));

  const startRegistration = (category) => {
    setSubmitError(null);
    setWizard({ ...EMPTY_WIZARD, open: true, category });
  };

  const resumeDraft = () => {
    const known = new Set(dancers.map((d) => d.id));
    setSubmitError(null);
    setWizard({
      open: true,
      category: draft.category,
      step: Math.min(Math.max(Number(draft.step) || 1, 1), 3),
      name: draft.name || '',
      choreographer: draft.choreographer || '',
      dancers: (draft.dancers || []).filter((dancerId) => known.has(dancerId)),
    });
  };

  const selection = wizard.category
    ? evaluateSelection(dancers, wizard.dancers, wizard.category.ageCategory, competition.date, meta.ageRules)
    : { valid: false, problems: [], count: 0 };
  const step1Valid = wizard.name.trim() !== '' && wizard.choreographer.trim() !== '';
  const leagueProblem =
    wizard.category && leagueCheck.key === leagueCheckKey(wizard.name, wizard.category) ? leagueCheck.problem : null;

  const goToStep = (step) => {
    setSubmitError(null);
    setWizard((w) => ({ ...w, step }));
  };

  const continueFromStep1 = async () => {
    if (!step1Valid || checkingLeague) return;
    setCheckingLeague(true);
    const problem = await checkLeague(wizard.name, wizard.category);
    setCheckingLeague(false);
    if (!problem) goToStep(2);
  };

  const handleStep1Submit = (e) => {
    e.preventDefault();
    continueFromStep1();
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    if (!selection.valid) {
      setSubmitError(selection.problems.join(' '));
      return;
    }
    submittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError(null);
    // The name may have changed since the last check (e.g. a resumed draft).
    if (await checkLeague(wizard.name, wizard.category)) {
      submittingRef.current = false;
      setIsSubmitting(false);
      return;
    }
    try {
      const { category } = wizard;
      const response = await choreographiesApi.create({
        name: wizard.name.trim(),
        choreographer: wizard.choreographer.trim(),
        discipline: category.discipline,
        ageCategory: category.ageCategory,
        league: normLeague(category.league),
        dancers: wizard.dancers,
        competitionId: competition.id,
      });
      removeKey(draftKey);
      toast.success(
        mode === 'pre'
          ? 'Choreografie je předregistrovaná. Po otevření registrace ji v detailu zaregistrujte.'
          : 'Choreografie je přihlášená. Teď nahrajte hudbu a potvrďte prezenci.'
      );
      submittingRef.current = false;
      navigate(`/choreographies/${response.data.id}`);
    } catch (err) {
      submittingRef.current = false;
      setSubmitError(getErrorMessage(err));
      setIsSubmitting(false);
    }
  };

  // --- wizard ---------------------------------------------------------------------------
  const wizardCategoryText = wizard.category
    ? categoryLabel(wizard.category.discipline, wizard.category.ageCategory, wizard.category.league)
    : '';
  const selectedNames = dancers
    .filter((d) => wizard.dancers.includes(d.id))
    .map((d) => `${d.firstName} ${d.lastName}`)
    .join(', ');

  let wizardFooter = null;
  if (wizard.step === 1) {
    wizardFooter = (
      <Button onClick={continueFromStep1} disabled={!step1Valid || Boolean(leagueProblem)} loading={checkingLeague}>
        Pokračovat
      </Button>
    );
  } else if (wizard.step === 2) {
    wizardFooter = (
      <>
        <Button variant="secondary" onClick={() => goToStep(1)}>
          Zpět
        </Button>
        <Button onClick={() => goToStep(3)} disabled={!selection.valid}>
          Pokračovat
        </Button>
      </>
    );
  } else {
    wizardFooter = (
      <>
        <Button variant="secondary" onClick={() => goToStep(2)} disabled={isSubmitting}>
          Zpět
        </Button>
        <Button onClick={handleSubmit} loading={isSubmitting} disabled={!selection.valid || Boolean(leagueProblem)}>
          {isSubmitting ? 'Odesílám…' : mode === 'pre' ? 'Dokončit předregistraci' : 'Dokončit registraci'}
        </Button>
      </>
    );
  }

  const categoryAction = (category) => {
    if (isGrandFinale) return null;
    const label = categoryLabel(category.discipline, category.ageCategory, category.league);
    if (mode === 'open') {
      return (
        <Button
          variant="secondary"
          className="w-full sm:w-auto"
          onClick={() => startRegistration(category)}
          aria-label={`Přihlásit choreografii – ${label}`}
        >
          Přihlásit
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      );
    }
    if (mode === 'pre') {
      return (
        <Button
          variant="secondary"
          className="w-full sm:w-auto"
          onClick={() => startRegistration(category)}
          aria-label={`Předregistrovat choreografii – ${label}`}
        >
          Předregistrovat
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      );
    }
    return null;
  };

  const showFilters = !isGrandFinale && categories.length > 9;

  return (
    <div className="space-y-6">
      <BackButton to="/competitions" label="Zpět na soutěže" />

      <PageHeader title={competition.name}>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <CompetitionTypeBadge type={competition.type} />
          <RegistrationBadge competition={competition} />
          <CapacityBadge competition={competition} />
        </div>
        <div className="mt-3 flex flex-col gap-2 text-sm text-gray-700 sm:flex-row sm:flex-wrap sm:gap-x-6">
          {competition.location && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-4 w-4 text-gray-500" aria-hidden="true" />
              {competition.location}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays className="h-4 w-4 text-gray-500" aria-hidden="true" />
            {formatDateLong(competition.date)}
          </span>
          {mode === 'pre' && competition.registrationStart && (
            <span>Registrace se otevře {formatDate(competition.registrationStart)}</span>
          )}
          {(mode === 'open' || mode === 'pre' || mode === 'full') && <DeadlineText date={competition.registrationEnd} />}
        </div>
      </PageHeader>

      {isGrandFinale && (
        <Alert tone="info" title="Grand Finále">
          Do Grand Finále se nepřihlašuje přímo. Choreografie se na něj musí nejprve kvalifikovat v semifinále. Kvalifikované
          choreografie přihlásíte v sekci{' '}
          <Link to="/qualified" className="font-medium underline">
            Grand Finále
          </Link>
          .
        </Alert>
      )}
      {!isGrandFinale && mode === 'pre' && (
        <Alert tone="info" title="Předregistrace">
          Registrace se otevře {competition.registrationStart ? formatDate(competition.registrationStart) : 'později'}. Už teď si
          můžete choreografii předregistrovat. Po otevření registrace ji ale musíte v detailu choreografie zaregistrovat – teprve
          pak se počítá do soutěže (pořadí registrace může rozhodovat o zvýhodněném startovném).
        </Alert>
      )}
      {!isGrandFinale && mode === 'closed' && (
        <Alert tone="info" title="Registrace uzavřena">
          Registrace do této soutěže je uzavřena
          {competition.registrationEnd ? ` (uzávěrka ${formatDate(competition.registrationEnd)})` : ''}. Nové přihlášky už
          nelze vytvořit, rozpracované choreografie dokončete v sekci{' '}
          <Link to="/choreographies" className="font-medium underline">
            Choreografie
          </Link>
          .
        </Alert>
      )}
      {!isGrandFinale && mode === 'full' && (
        <Alert tone="warning" title="Kapacita naplněna">
          Kapacita této soutěže je naplněna, další choreografie už nelze přihlásit.
        </Alert>
      )}
      {mode === 'finished' && <Alert tone="info">Soutěž je ukončená.</Alert>}

      {draftUsable && !wizard.open && (
        <Alert tone="info" title="Máte rozepsanou přihlášku">
          <p>
            Kategorie {categoryLabel(draft.category.discipline, draft.category.ageCategory, draft.category.league)}
            {draft.name ? `, název „${draft.name}“` : ''}.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button onClick={resumeDraft}>Pokračovat v přihlášce</Button>
            <Button variant="secondary" onClick={discardDraft}>
              Zahodit
            </Button>
          </div>
        </Alert>
      )}

      {myEntries.length > 0 && (
        <section className="rounded-lg bg-white p-4 shadow sm:p-6" aria-labelledby="my-entries-title">
          <h2 id="my-entries-title" className="mb-3 text-base font-semibold text-gray-900">
            Vaše choreografie v této soutěži ({myEntries.length})
          </h2>
          <ul className="divide-y divide-gray-100">
            {myEntries.map((entry) => (
              <li key={entry.id}>
                <Link
                  to={`/choreographies/${entry.id}`}
                  className="flex flex-col gap-1 py-2 hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between"
                >
                  <span>
                    <span className="font-medium text-gray-900">{entry.name}</span>
                    <span className="block text-sm text-gray-600">
                      {categoryLabel(entry.discipline, entry.ageCategory, entry.league)}
                    </span>
                  </span>
                  <StatusBadge kind="choreography" status={entry.status} />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="rounded-lg bg-white p-4 shadow sm:p-6" aria-labelledby="categories-title">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <h2 id="categories-title" className="text-lg font-semibold text-gray-900">
            {isGrandFinale ? 'Kategorie Grand Finále' : 'Kategorie'}
          </h2>
          {showFilters && (
            <div className="flex flex-wrap gap-3">
              <label className="text-sm text-gray-700">
                <span className="mr-2">Věk</span>
                <select
                  value={ageFilter}
                  onChange={(e) => setAgeFilter(e.target.value)}
                  className="rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Všechny</option>
                  {AGE_CATEGORIES.map((age) => (
                    <option key={age} value={age}>
                      {ageCategoryLabel(age)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm text-gray-700">
                <span className="mr-2">Liga</span>
                <select
                  value={leagueFilter}
                  onChange={(e) => setLeagueFilter(e.target.value)}
                  className="rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Všechny</option>
                  {LEAGUES.map((league) => (
                    <option key={league} value={league}>
                      {leagueLabel(league)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>

        {filtered.length === 0 && <p className="text-sm text-gray-600">Filtru neodpovídá žádná kategorie.</p>}

        <div className="space-y-6">
          {ageGroups.map((group) => (
            <div key={group.age}>
              {ageGroups.length > 1 && (
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-600">
                  {group.age === 'OTHER' ? 'Ostatní' : ageCategoryLabel(group.age)}
                </h3>
              )}
              <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                {group.items.map((category) => {
                  const entries = entriesIn(category);
                  return (
                    <li
                      key={`${category.discipline}-${category.ageCategory}-${category.league}`}
                      className="flex flex-col justify-between gap-3 rounded-lg border border-gray-200 p-4"
                    >
                      <div>
                        <p className="font-medium text-gray-900">{disciplineLabel(category.discipline)}</p>
                        <p className="text-sm text-gray-600">
                          {ageCategoryLabel(category.ageCategory)} · {leagueLabel(category.league)}
                        </p>
                        {entries > 0 && (
                          <p className="mt-1 text-xs font-medium text-indigo-700">
                            Už zde máte {countLabel(entries, ['choreografii', 'choreografie', 'choreografií'])}
                          </p>
                        )}
                      </div>
                      {categoryAction(category)}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
        {isGrandFinale && (
          <p className="mt-4 flex items-center gap-2 text-sm text-gray-600">
            <Trophy className="h-4 w-4 text-amber-600" aria-hidden="true" />
            Přihlašuje se v sekci Grand Finále.
          </p>
        )}
      </section>

      {wizard.open && (
        <Modal
          title="Registrace choreografie"
          onClose={closeWizard}
          size="xl"
          closeOnBackdrop={false}
          initialFocusRef={nameRef}
          footer={wizardFooter}
        >
          <p className="mb-4 text-sm text-gray-600">
            {wizardCategoryText} · {competition.name}
          </p>
          <Stepper step={wizard.step} />

          {wizard.step === 1 && (
            <form onSubmit={handleStep1Submit} className="space-y-4" noValidate>
              <TextField
                ref={nameRef}
                label="Název choreografie"
                value={wizard.name}
                onChange={(e) => setWizard((w) => ({ ...w, name: e.target.value }))}
                maxLength={100}
                autoComplete="off"
                required
              />
              <TextField
                label="Choreograf/ka nebo choreografové"
                value={wizard.choreographer}
                onChange={(e) => setWizard((w) => ({ ...w, choreographer: e.target.value }))}
                maxLength={255}
                placeholder="např. Jan Novák, Eva Svobodová"
                autoComplete="off"
                required
              />
              {leagueProblem && <Alert tone="error">{leagueProblem}</Alert>}
              {/* lets Enter continue to the next step */}
              <button type="submit" className="hidden" tabIndex={-1} aria-hidden="true" />
            </form>
          )}

          {wizard.step === 2 && (
            <div>
              <h3 className="mb-3 text-base font-semibold text-gray-900">Vyberte tanečníky</h3>
              <DancerPicker
                dancers={dancers}
                competitionDate={competition.date}
                ageCategory={wizard.category.ageCategory}
                value={wizard.dancers}
                onChange={(ids) => setWizard((w) => ({ ...w, dancers: ids }))}
              />
            </div>
          )}

          {wizard.step === 3 && (
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-gray-900">Kontrola registrace</h3>
              <dl className="divide-y divide-gray-100 rounded-md border border-gray-200 text-sm">
                {[
                  ['Soutěž', `${competition.name} · ${formatDate(competition.date)}`],
                  ['Kategorie', wizardCategoryText],
                  ['Název', wizard.name.trim()],
                  ['Choreograf/ka', wizard.choreographer.trim()],
                  ['Tanečníci', countLabel(wizard.dancers.length, ['tanečník', 'tanečníci', 'tanečníků'])],
                ].map(([term, value]) => (
                  <div key={term} className="flex flex-col gap-0.5 px-3 py-2 sm:flex-row sm:justify-between sm:gap-4">
                    <dt className="text-gray-600">{term}</dt>
                    <dd className="font-medium text-gray-900 sm:text-right">{value}</dd>
                  </div>
                ))}
              </dl>
              {selectedNames && <p className="text-sm text-gray-600">{selectedNames}</p>}
              <Alert tone="info">
                {mode === 'pre'
                  ? 'Choreografie se uloží jako předregistrace. Až se registrace otevře, zaregistrujte ji v detailu choreografie.'
                  : 'Po odeslání nahrajte v detailu choreografie hudbu (MP3) a potvrďte online prezenci.'}
              </Alert>
              {leagueProblem && <Alert tone="error">{leagueProblem}</Alert>}
              {submitError && (
                <Alert tone="error" title="Registraci se nepodařilo dokončit">
                  {submitError}
                </Alert>
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

export default CompetitionDetail;
